import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Divider } from '@mui/material';

function OrderDetailsModal({ open, loading, data, onClose }) {
  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const order = data?.order || null
  const totals = data?.totals || null
  const terms = Array.isArray(data?.payment_terms) ? data.payment_terms : []
  const issueItems = Array.isArray(data?.issue_invoice_items) ? data.issue_invoice_items : []
  const invoiceItems = Array.isArray(data?.invoice_items) ? data.invoice_items : []
  const payments = Array.isArray(data?.payments) ? data.payments : []

  const totalTermsAmtRs = terms.reduce((sum, t) => sum + (parseFloat(t.amount_rs || 0) || 0), 0)

  const issueTotals = issueItems.reduce((acc, it) => {
    acc.count += (parseFloat(it.item_count || 0) || 0)
    acc.discount += (parseFloat(it.discount_amount || 0) || 0)
    acc.total += (parseFloat(it.total || 0) || 0)
    return acc
  }, { count: 0, discount: 0, total: 0 })

  const invTotals = invoiceItems.reduce((acc, it) => {
    acc.count += (parseFloat(it.item_count || 0) || 0)
    acc.discount += (parseFloat(it.discount_amount || 0) || 0)
    acc.total += (parseFloat(it.total || 0) || 0)
    return acc
  }, { count: 0, discount: 0, total: 0 })

  const payTotals = payments.reduce((sum, p) => sum + (parseFloat(p.amount_paid || 0) || 0), 0)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Order Details</DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography variant="body2" color="textSecondary">Loading...</Typography>
        ) : !data ? (
          <Typography variant="body2" color="textSecondary">No details</Typography>
        ) : (
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Order</Typography>
            <Box mb={2} p={2} bgcolor="#f0f4ff" borderRadius={1} border={1} borderColor="grey.300">
              <Box display="flex" gap={3} flexWrap="wrap">
                <Typography variant="caption" color="textSecondary">Company: {order?.company_name ?? '-'}</Typography>
                {(order?.covered_company_names || []).length > 1 ? (
                  <Typography variant="caption" color="textSecondary">
                    Institutes: {(order.covered_company_names || []).join(', ')}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="textSecondary">Service: {order?.service_name ?? '-'}</Typography>
                <Typography variant="caption" color="textSecondary">Plan: {order?.plan_name ?? '-'}</Typography>
                <Typography variant="caption" color="textSecondary">Order Date: {order?.order_date ?? '-'}</Typography>
                <Typography variant="caption" color="textSecondary">Item Count: {order?.item_count ?? '-'}</Typography>
                <Typography variant="caption" color="textSecondary">Service Amount: {formatAmount(order?.service_amount ?? 0)}</Typography>
                <Typography variant="caption" color="textSecondary">Tax %: {order?.service_gst_percentage ?? '-'}</Typography>
                <Typography variant="caption" color="textSecondary">Total: {formatAmount(order?.total_amount ?? 0)}</Typography>
                <Typography variant="caption" color="primary" style={{ fontWeight: 'bold' }}>
                  Overall Balance Left: {formatAmount(totals?.balance_amount ?? 0)}
                </Typography>
                <Typography variant="caption" color="primary" style={{ fontWeight: 'bold' }}>
                  Balance Left (as of {totals?.as_of_date ?? 'today'}): {formatAmount(totals?.balance_amount_till_today ?? 0)}
                </Typography>
              </Box>
              <Box mt={1} display="flex" gap={3} flexWrap="wrap">
                <Typography variant="caption" color="textSecondary">Paid (cash): {formatAmount(totals?.paid_amount ?? 0)}</Typography>
                <Typography variant="caption" color="textSecondary">Discount considered: {formatAmount(totals?.invoice_discount_total ?? 0)}</Typography>
                <Typography variant="caption" color="textSecondary">Paid + Discount: {formatAmount(totals?.paid_with_discount ?? 0)}</Typography>
              </Box>
            </Box>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Term Details</Typography>
            <Box mb={1} display="flex" gap={3} flexWrap="wrap">
              <Typography variant="caption" color="textSecondary">Total Terms: {terms.length}</Typography>
              <Typography variant="caption" color="textSecondary">Total Amount Rs: {formatAmount(totalTermsAmtRs)}</Typography>
            </Box>
            {terms.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No terms</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Term</TableCell>
                    <TableCell>Receive Date</TableCell>
                    <TableCell align="right">Amount %</TableCell>
                    <TableCell align="right">Amount Rs</TableCell>
                    <TableCell align="right">Total Amount Rs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {terms.map((t, idx) => (
                    <TableRow key={`term-${idx}`}>
                      <TableCell>{t.term_name || '-'}</TableCell>
                      <TableCell>{t.term_receive_date || '-'}</TableCell>
                      <TableCell align="right">{t.amount_percentage ?? '-'}</TableCell>
                      <TableCell align="right">{t.amount_rs ? formatAmount(t.amount_rs) : '-'}</TableCell>
                      <TableCell align="right">{t.total_amount ? formatAmount(t.total_amount) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Box mt={2} mb={2}><Divider /></Box>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Involved Issue Invoice Items</Typography>
            <Box mb={1} display="flex" gap={3} flexWrap="wrap">
              <Typography variant="caption" color="textSecondary">Total Count: {issueTotals.count.toFixed(2)}</Typography>
              <Typography variant="caption" color="textSecondary">Total Discount: {formatAmount(issueTotals.discount)}</Typography>
              <Typography variant="caption" color="textSecondary">Total Amount: {formatAmount(issueTotals.total)}</Typography>
            </Box>
            {issueItems.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No issued invoice items</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Issue Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issueItems.map((it, idx) => (
                    <TableRow key={`iss-${idx}`}>
                      <TableCell>{it.invoice_number || '-'}</TableCell>
                      <TableCell>{it.issue_date || '-'}</TableCell>
                      <TableCell>{it.status || '-'}</TableCell>
                      <TableCell align="right">{it.item_count ?? '-'}</TableCell>
                      <TableCell align="right">{formatAmount(it.discount_amount ?? 0)}</TableCell>
                      <TableCell align="right">{formatAmount(it.total ?? 0)}</TableCell>
                      <TableCell>{it.notes ?? ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Box mt={2} mb={2}><Divider /></Box>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Involved Invoice Items</Typography>
            <Box mb={1} display="flex" gap={3} flexWrap="wrap">
              <Typography variant="caption" color="textSecondary">Total Count: {invTotals.count.toFixed(2)}</Typography>
              <Typography variant="caption" color="textSecondary">Total Discount: {formatAmount(invTotals.discount)}</Typography>
              <Typography variant="caption" color="textSecondary">Total Amount: {formatAmount(invTotals.total)}</Typography>
            </Box>
            {invoiceItems.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No invoice items</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Issue Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceItems.map((it, idx) => (
                    <TableRow key={`inv-${idx}`}>
                      <TableCell>{it.invoice_number || '-'}</TableCell>
                      <TableCell>{it.issue_date || '-'}</TableCell>
                      <TableCell>{it.status || '-'}</TableCell>
                      <TableCell align="right">{it.item_count ?? '-'}</TableCell>
                      <TableCell align="right">{formatAmount(it.discount_amount ?? 0)}</TableCell>
                      <TableCell align="right">{formatAmount(it.total ?? 0)}</TableCell>
                      <TableCell>{it.notes ?? ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Box mt={2} mb={2}><Divider /></Box>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Involved Payments</Typography>
            <Box mb={1} display="flex" gap={3} flexWrap="wrap">
              <Typography variant="caption" color="textSecondary">Total Payments: {payments.length}</Typography>
              <Typography variant="caption" color="textSecondary">Total Paid Amount: {formatAmount(payTotals)}</Typography>
            </Box>
            {payments.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No payments</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell align="right">Amount Paid</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p, idx) => (
                    <TableRow key={`pay-${idx}`}>
                      <TableCell>{p.payment_date ? String(p.payment_date).slice(0, 10) : '-'}</TableCell>
                      <TableCell>{p.method || '-'}</TableCell>
                      <TableCell>{p.reference || '-'}</TableCell>
                      <TableCell>{p.status || '-'}</TableCell>
                      <TableCell>{p.invoice_number || '-'}</TableCell>
                      <TableCell align="right">{formatAmount(p.amount_paid ?? 0)}</TableCell>
                      <TableCell>{p.notes ?? ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default OrderDetailsModal;



